import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { Tile } from "@carbon/react";
import { ArrowDown, ArrowUp } from "@carbon/icons-react";
import { PricePoint } from "../../../types";
import { toPriceIndex, IndexedFamily } from "./chartData";
import "./analyticsCharts.scss";

interface PriceIndexChartProps {
  priceSeries: PricePoint[];
  /** Active currency: only this currency's points are indexed (never mixed across currencies). */
  currency: string;
}

// Categorical palette, one stable color per family id. No `--cds-chart-*` tokens ship with this
// Carbon version, so the literal fallbacks are the Carbon categorical hues; the `var()` just
// leaves a theming hook and can never void the declaration.
const FAMILY_COLORS = [
  "var(--cds-chart-1, #6929c4)",
  "var(--cds-chart-2, #1192e8)",
  "var(--cds-chart-3, #005d5d)",
  "var(--cds-chart-4, #9f1853)",
  "var(--cds-chart-5, #fa4d56)",
  "var(--cds-chart-6, #570408)",
  "var(--cds-chart-7, #198038)",
  "var(--cds-chart-8, #002d9c)",
];
const BASELINE_COLOR = "var(--cds-text-secondary, #525252)";
const AXIS_COLOR = "var(--cds-text-secondary, #525252)";
// More lines than this is unreadable; the rest stay in the movers list.
const MAX_LINES = 5;

type Direction = "up" | "down" | "flat";

/** Rounds to the displayed precision first so "+0.0%" can never be classed as up. */
const describeChange = (changePct: number) => {
  const rounded = Math.round(changePct * 10) / 10 + 0; // `+ 0` normalises -0
  const direction: Direction =
    rounded > 0 ? "up" : rounded < 0 ? "down" : "flat";
  const text = `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`;
  return { direction, text };
};

const PriceIndexChart = ({ priceSeries, currency }: PriceIndexChartProps) => {
  const { t, i18n } = useTranslation();
  const { rows, families } = toPriceIndex(priceSeries, currency, i18n.language);
  const title = t("analytics.charts.priceIndexTitle");

  if (families.length === 0) {
    return (
      <Tile className="analytics-chart">
        <h3 className="analytics-chart__title">{title}</h3>
        <p className="analytics-chart__empty">
          {t("analytics.charts.noPriceData")}
        </p>
      </Tile>
    );
  }

  // Color follows the family's rank among ALL families (by id), not its position among the
  // plotted ones, so a family keeps its color whichever lines the cap lets through.
  const colorOf = new Map<string, string>(
    [...families]
      .map((f) => f.id)
      .sort()
      .map((id, i) => [id, FAMILY_COLORS[i % FAMILY_COLORS.length]])
  );
  const plotted: IndexedFamily[] = families.slice(0, MAX_LINES);

  return (
    <Tile className="analytics-chart">
      <h3 className="analytics-chart__title">{title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={rows}
          margin={{ top: 20, right: 30, left: 10, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--cds-border-subtle, #e0e0e0)"
          />
          <XAxis
            dataKey="label"
            fontSize={12}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <YAxis
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
            tickFormatter={(v) => Math.round(v).toString()}
            domain={["auto", "auto"]}
          />
          <Tooltip
            formatter={(value, name) => [Number(value).toFixed(1), name]}
          />
          <Legend />
          <ReferenceLine
            y={100}
            stroke={BASELINE_COLOR}
            strokeDasharray="6 4"
            ifOverflow="extendDomain"
            label={{
              value: t("analytics.charts.priceIndexBaseline"),
              position: "insideTopRight",
              fill: AXIS_COLOR,
              fontSize: 11,
            }}
          />
          {/* No entry animation (deterministic first paint); `connectNulls` bridges months a family has no price; `dot` keeps a lone point visible (a line with one point draws no path). */}
          {plotted.map((f) => (
            <Line
              key={f.id}
              type="monotone"
              dataKey={f.id}
              name={f.family}
              stroke={colorOf.get(f.id)}
              strokeWidth={2}
              dot={{ r: 3, fill: colorOf.get(f.id), stroke: colorOf.get(f.id) }}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <h4 className="analytics-chart__title">{t("analytics.charts.movers")}</h4>
      <ul className="analytics-movers" data-testid="price-movers">
        {families.map((f) => {
          const { direction, text } = describeChange(f.changePct);
          return (
            <li
              key={f.id}
              className="analytics-movers__item"
              data-family={f.id}
              data-direction={direction}
            >
              <span
                className="analytics-movers__swatch"
                style={{ background: colorOf.get(f.id) }}
                aria-hidden="true"
              />
              <span className="analytics-movers__name" title={f.family}>
                {f.family}
              </span>
              <span
                className="analytics-movers__change"
                data-direction={direction}
              >
                {direction === "up" && (
                  <ArrowUp
                    size={16}
                    aria-label={t("analytics.charts.moversUp")}
                  />
                )}
                {direction === "down" && (
                  <ArrowDown
                    size={16}
                    aria-label={t("analytics.charts.moversDown")}
                  />
                )}
                {text}
              </span>
            </li>
          );
        })}
      </ul>
    </Tile>
  );
};

export default PriceIndexChart;
